import { CollaborationResult } from '@descomplica/microfrontend-core';

import { EssayInput, EssayMediaInput } from '@/presentation/screens/essay/types';

export interface IPegasusGateway {
  getApplication(): Promise<CollaborationResult<ApplicationDto>>;
  getLibraryByClassifierGuid(guid: string): Promise<CollaborationResult<ClassifierDto[]>>;
  getHighestClassificationLevels(): Promise<CollaborationResult<ClassifierDto[]>>;
  getStudyPlan(guid: string): Promise<CollaborationResult<StudyPlanDto>>;
  getLiveStreamingByGuid(guid: string): Promise<CollaborationResult<LiveStreamingDto>>;
  getExercisesByClassifier(guid: string): Promise<CollaborationResult<QuizDto[]>>;
  createEssay(input: EssayInput): Promise<CollaborationResult<EssayDto>>;
  createEssayMedia(input: EssayMediaInput): Promise<CollaborationResult<EssayMediaDto>>;
  createEssayDraft(input: EssayInput): Promise<CollaborationResult<EssayDto>>;
  updateEssay(input: EssayInput): Promise<CollaborationResult<EssayDto>>;
  deleteEssayDraft(guid: string): Promise<CollaborationResult<void>>;
  getAllEssayThemes(): Promise<CollaborationResult<EssayThemeDto[]>>;
  getEssayTheme(guid: string): Promise<CollaborationResult<EssayThemeDto>>;
  getAllEssays(): Promise<CollaborationResult<AllEssayDto>>;
  getEssay(guid: string): Promise<CollaborationResult<EssayDto>>;
  getEssayHighlights(): Promise<CollaborationResult<EssayHighlightDto[]>>;
  getStudentClassByClassifierGuid(guid: string): Promise<CollaborationResult<any>>;
  getCurrentStudentClasses(classifierGuid: string): Promise<CollaborationResult<StudentClassesDto>>;
}

export type ApplicationDto = {
  guid: string;
  name: string;
  slug: string;
  description?: string;
  organization: {
    guid: string;
    name: string;
    slug: string;
    logo: string;
  };
};

export type CourseDto = {
  guid: string;
  name: string;
  thumbnail?: string;
  totalLessons?: number;
  lessons: LessonDto[];
  slug: string;
  isEnabled: boolean;
  totalHours: number;
  classifier?: ClassifierDto;
  percentualProgress?: number;
  isCompleted?: boolean;
};

export type LessonDto = {
  guid: string;
  lessonType: LessonType;
  thumbnail: string;
  isComplete?: boolean;
  percentualProgress?: number;
};

export enum LessonType {
  LIVE = 'LIVE',
  PRE_RECORDED = 'PRE_RECORDED',
}

export enum ProcessStatusEnum {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export type ClassifierDto = {
  guid: string;
  name: string;
  classificationLevel: ClassificationLevelDto;
  childClassifiers: ClassifierDto[];
  courses: CourseDto[];
};

export type ClassificationLevelDto = {
  guid: string;
  name: string;
  level: string;
};

export type StudyPlanDto = {
  guid: string;
  classificationLevel: ClassificationLevelDto;
  weeks: WeeksDto[];
  startDate: Date;
  totalWeeks: number;
  totalLessons: number;
  lives: LiveStreamingDto[];
};

export type WeeksDto = {
  order: number;
  planned: CourseDto[];
};

export type LiveStreamingDto = {
  guid: string;
  name: string;
  teacher?: string;
  thumbnail: string;
  streaming: {
    guid: string;
    title: string;
    description?: string;
    streamingAccountId: string;
    streamingId: string;
    order: number;
    startStreaming: Date;
    endStreaming: Date;
    lessonStatus: string;
  };
  slug: string;
  course: {
    guid: string;
    name: string;
    slug: string;
    classifiers: ClassifierDto[];
  };
  student_id?: string;
  user?: string;
  email?: string;
};

export type UserDto = {
  guid: string;
  name: string;
  email: string;
  permissions: {
    canViewAdmin: boolean;
    canViewEssay?: any;
  };
};

export enum ExerciseType {
  LESSON,
  EXAM,
  QUIZ,
}

export type ExerciseExamConfigDto = {
  guid: string;
  exercises_guid: string;
  start_date?: Date;
  expiration_date?: Date;
  interval_minutes?: number;
};

export type QuizDto = {
  guid: string;
  introduction?: string;
  isEnabled?: boolean;
  type: ExerciseType;
  examExercisesConfig?: ExerciseExamConfigDto;
  title?: string;
  classifier_guid?: string;
  studentFinished: boolean;
  correctAnswers: number;
  totalQuestions: number;
};

export type AllEssayDto = {
  data?: EssayDto[];
  metadata?: {
    enemFeedback: {
      overallScore: number;
    };
  };
};

export type EssayDto = {
  guid?: string;
  title?: string;
  essayContent?: string;
  createdAt?: Date;
  essayTheme?: EssayThemeDto;
  essayStatus?: EssayType; //
  essayFeedback?: EssayFeedbackDto;
  processStatus?: ProcessStatusEnum;

  id?: string;
  content?: string;
  status?: string;
  topic?: {
    id: string;
    name: string;
  };
  enemFeedback?: {
    grammarFeedback?: any[];
    overallScore?: number;
    overallComment?: string;
    competence1Score?: number;
    competence1Comment?: string;
    competence2Score?: number;
    competence2Comment?: string;
    competence3Score?: number;
    competence3Comment?: string;
    competence4Score?: number;
    competence4Comment?: string;
    competence5Score?: number;
    competence5Comment?: string;
  };
};

export type EssayThemeDto = {
  motivationalTexts?: MotivationalTextDto[];
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  coverImageDescription?: string;
  modelEssays?: any[];
  isHighlight?: boolean;
  type?: {
    id: string;
    name: string;
  };
};

export type EssayHighlightDto = {
  id?: string;
  topic?: {
    imageUrl?: string;
    id?: string;
    name?: string;
  };
  type?: {
    id?: string;
    name?: string;
    slug?: string;
  };
  status?: string;
};

export type MotivationalTextDto = {
  guid: string;
  content: string;
  images: {
    url: string;
    description?: string;
    source?: string;
  }[];
};

export type EssayFeedbackDto = {
  generalScore: string;
  generalCorrection: string;
  contentCorrection: string;
  firstCompetence: CompetenceDto;
  secondCompetence: CompetenceDto;
  thirdCompetence: CompetenceDto;
  fourthCompetence: CompetenceDto;
  fifthCompetence: CompetenceDto;
  score: string;
};

export type CompetenceDto = {
  score: string;
  competenceCorrection: string;
};

export enum EssayType {
  SENT = 'SENT',
  DONE = 'DONE',
  DRAFT = 'DRAFT',
}

export type StudentClassesDto = {
  guid: string;
  title?: string;
  startDate: Date;
  endDate: Date;
  tags: string[];
  tagsWithScope: string[];
  classifierGuid: string;
  classifier?: ClassifierDto;
  deletedAt?: Date;
  scopeGuid?: string;
};

export type EssayMediaDto = {
  id: string;
  applicationId: string;
  text: string;
  imageUrl: string;
};
